import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import cs from 'classnames';
import { injectStoresV2 } from '../../../stores/injectStoresV2';
import { SearchWithSuggestionsStore } from '../../../stores/SearchWithSuggestionsStore';
import { SuggestionsList } from '../SuggestionsList/SuggestionsList';
import { SearchSubmitButton } from '../SearchSubmitButton/SearchSubmitButton';
import { HeaderStore } from '../../../stores/HeaderStore';
import { suggestionsDropdown } from './SuggestionsDropdown.dataHooks';

import s from './SuggestionsDropdown.scss';

export const dropdownId = 'search-dropdown';

type SuggestionsDropdownProps = {
  searchWithSuggestionsStore: SearchWithSuggestionsStore;
  headerStore: HeaderStore;
  dataHook?: string;
  onSearch: () => void;
};
const dropdownMenuEnterTimeout = parseInt(s.dropdownEnterTimeout, 10);
const dropdownMenuLeaveTimeout = parseInt(s.dropdownLeaveTimeout, 10);

const SuggestionsDropdownCmp: React.FC<SuggestionsDropdownProps> = ({
  dataHook,
  searchWithSuggestionsStore,
  headerStore,
  onSearch,
}) => {
  const { searchSuggestions, isDropdownOpened, preSelectedSuggestionIndex } = searchWithSuggestionsStore;
  const { searchCriteria, isSearchInputFocused, isSticky } = headerStore;
  const isOpened = searchCriteria.trim() !== '' && isDropdownOpened && isSearchInputFocused;
  const dropdownRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (!preSelectedSuggestionIndex && dropdownRef.current) {
      dropdownRef.current.scrollTop = 0;
    }
  }, [preSelectedSuggestionIndex]);

  return (
    <CSSTransition
      in={isOpened}
      classNames={{
        enter: s.dropdownEnter,
        enterActive: s.dropdownEnterActive,
        enterDone: s.dropdownEnterDone,
        exit: s.dropdownLeave,
        exitActive: s.dropdownLeaveActive,
      }}
      timeout={{ enter: dropdownMenuEnterTimeout, exit: dropdownMenuLeaveTimeout }}
      mountOnEnter
      unmountOnExit
    >
      <div
        id={dropdownId}
        className={cs(s.dropdownWrapper, {
          [s.leftAligned]: !isSticky,
          [s.extraTopMargin]: !isSticky,
          [s.newUiSearch]: true,
        })}
        data-hook={cs(dataHook, suggestionsDropdown.dropdown({ opened: isOpened }))}
        aria-expanded={isOpened}
      >
        <div
          className={cs(s.dropdown, {
            [s.noResultsDropdown]: !searchSuggestions.length,
            [s.roundedBorders]: !isSticky,
          })}
        >
          <SearchSubmitButton dataHook={suggestionsDropdown.searchButton()} />
          <div className={s.dropdownList} ref={dropdownRef}>
            {!!searchSuggestions.length && (
              <SuggestionsList
                dataHook={suggestionsDropdown.resultsList()}
                suggestions={searchSuggestions}
                onSearch={onSearch}
              />
            )}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export const SuggestionsDropdown = injectStoresV2('searchWithSuggestionsStore', 'headerStore')(SuggestionsDropdownCmp);
